import React, {useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import { FiUploadCloud, FiX, FiCheck, FiArrowLeft } from 'react-icons/fi';
import { toast } from 'react-toastify';

const CreateRequest = () => {  
  const [formData, setFormData] = useState({
    request_name: '',
    category_name: '',
    description: ''
  });
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const navigate = useNavigate();

  // Fetch user info on component mount
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/users/me`, {
          withCredentials: true
        });
        setUserInfo(response.data);
      } catch (error) {
        if (error.response?.status === 401) {
          navigate('/login');
        } else {
          toast.error('Failed to load user information');
        }
      }
    };

    fetchUserInfo();
  }, [navigate]);

  // Handle file drop
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'image/*': ['.jpeg', '.jpg', '.png'],
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
    },
    maxFiles: 1,
    maxSize: 25 * 1024 * 1024, // 25MB
    onDrop: acceptedFiles => {
      setFile(acceptedFiles[0]);
    }
  });

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!userInfo) {
      toast.error('User information not loaded. Please try again.');
      return;
    }

    if (!file) {
      toast.error('Please select a file to upload');
      return;
    }

    if (!formData.request_name || !formData.category_name) {
      toast.error('request name and category are required');
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataObj = new FormData();
      formDataObj.append('request_file', file);
      formDataObj.append('registration_id', userInfo.registrationId || userInfo.registration_id); // Handle both cases
      formDataObj.append('user_id', userInfo.id);
      formDataObj.append('request_name', formData.request_name);
      formDataObj.append('category_name', formData.category_name);
      formDataObj.append('description', formData.description);

      // Make sure your API endpoint is correct
    await axios.post(
        `${process.env.REACT_APP_API_URL}/api/request/upload`, // Changed from /api/requests/upload to /api/request/upload
        formDataObj,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          withCredentials: true
        }
      );

      toast.success('request uploaded successfully!');
      navigate('/user/requests');
    } catch (err) {
      console.error('Upload error:', err);
      
      if (err.response?.status === 401) {
        navigate('/user/login');
      } else if (err.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error('Failed to upload request. Please check your connection and try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6">
    <h5 onClick={() => navigate(-1)} className="text-blue-600 p-1 cursor-pointer rounded-lg "><FiArrowLeft/> back</h5>
      <div className="bg-white rounded-xl shadow-md p-6 md:p-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Upload New request</h1>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* request Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                request Name *
              </label>
              <input
                type="text"
                name="request_name"
                value={formData.request_name}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Quarterly requests, etc."
                required
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category *
              </label>
              <input
                type="text"
                name="category_name"
                value={formData.category_name}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Proposal, Financial, etc."
                required
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Brief description of the ..."
            />
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              request File *
            </label>
            <div 
              {...getRootProps()} 
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400'
              }`}
            >
              <input {...getInputProps()} />
              <div className="flex flex-col items-center justify-center space-y-2">
                <FiUploadCloud className="w-10 h-10 text-gray-400" />
                {file ? (
                  <div className="flex items-center space-x-2 text-green-600">
                    <FiCheck />
                    <span>{file.name}</span>
                    <button 
                      type="button" 
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(null);
                      }}
                      className="text-red-500 hover:text-red-700"
                    >
                      <FiX />
                    </button>
                  </div>
                ) : isDragActive ? (
                  <p className="text-blue-500">Drop the file here...</p>
                ) : (
                  <>
                    <p className="text-sm text-gray-600">
                      Drag & drop a file here, or click to select
                    </p>
                    <p className="text-xs text-gray-500">
                      PDF, Word, Excel, Images (max 25MB)
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting || !file || !userInfo}
              className={`px-6 py-2 rounded-lg text-white font-medium ${
                isSubmitting || !file || !userInfo
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700'
              } transition-colors flex items-center justify-center min-w-32`}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Uploading...
                </>
              ) : (
                'Upload request'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>

  )
}

export default CreateRequest;
